import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import { useTranslateOrganizationContentMutation } from '@/services/OrganizationManagementService';
import { useSnackbar } from 'notistack';
import { IOrganizationData } from '@/types/organization.types';

interface OrganizationPreviewProps extends Omit<ModalProps, 'children'> {
  formData: Partial<IOrganizationData>;
  source_language?: string;
  onTranslationsGenerated?: (
    translations: Record<
      string,
      Partial<{ organisation_name: string; description: string }>
    >
  ) => void;
}

interface LanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  formData: Partial<IOrganizationData>;
  translatedContent?: Record<
    string,
    {
      organisation_name?: string;
      description?: string;
    }
  >;
  isLoading: boolean;
  onEditContent?: (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => void;
}

interface TranslationPayload {
  description?: string;
  source_language: string;
}

interface EditValues {
  organisation_name?: string;
  description?: string;
}

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : undefined;
  }

  const maybeError = error as {
    data?: unknown;
    message?: unknown;
    error?: unknown;
  };

  if (maybeError.data) {
    if (typeof maybeError.data === 'string') return maybeError.data;

    if (typeof maybeError.data === 'object') {
      const data = maybeError.data as {
        message?: unknown;
        error?: unknown;
        warning_message?: unknown;
      };

      if (typeof data.warning_message === 'string') return data.warning_message;
      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  if (typeof maybeError.message === 'string') return maybeError.message;
  if (typeof maybeError.error === 'string') return maybeError.error;

  return undefined;
};

const hasTranslations = (
  value?: Record<
    string,
    Partial<{ organisation_name: string; description: string }>
  > | null
): value is Record<
  string,
  Partial<{ organisation_name: string; description: string }>
> => Boolean(value && Object.keys(value).length > 0);

const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  formData,
  translatedContent,
  isLoading,
  onEditContent,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>({});

  const langContent = useMemo(() => {
    return {
      // Organization name should always come from the form, not translations.
      organisation_name: formData.organisation_name,
      description:
        translatedContent && translatedContent[language.code]?.description
          ? translatedContent[language.code]?.description
          : formData.description,
    };
  }, [
    language.code,
    formData.organisation_name,
    formData.description,
    translatedContent,
  ]);

  const toggleEditMode = () => {
    if (!isEditing) {
      const initialValues: EditValues = {};
      if (langContent?.description)
        initialValues.description = langContent.description;
      setEditValues(initialValues);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    if (onEditContent) {
      Object.entries(editValues).forEach(([field, value]) => {
        onEditContent(language.code, field as keyof EditValues, value);
      });
      setIsEditing(false);
    }
  };

  const handleChange = (field: keyof EditValues, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const description = langContent?.description;

    const initialValues: EditValues = {};
    if (description) initialValues.description = description;
    setEditValues(initialValues);
  }, [langContent?.description]);

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-200 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h2 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isLoading && (
              <span className="ml-2 flex items-center text-xs text-blue-600 contrast:text-blue-200 font-medium">
                <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                translating...
              </span>
            )}
          </h2>
        </div>

        {/* Single Edit Icon for all fields */}
        {!isLoading && onEditContent && (
          <button
            onClick={toggleEditMode}
            className={`${isEditing
                ? 'bg-blue-100 dark:bg-blue-900'
                : 'bg-gray-100 dark:bg-gray-400'
              } text-blue-600 dark:text-blue-200 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 dark:hover:bg-blue-600 transition-colors`}
            title={isEditing ? 'Cancel editing' : 'Edit all fields'}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="p-5 flex justify-center">
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5">
          {isEditing ? (
            <>
              {/* Edit Mode View */}
              <div className="space-y-6">
                {/* Description Field */}
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    Description
                  </p>
                  <textarea
                    value={editValues.description || ''}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    className="w-full textarea p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[100px]"
                    placeholder="Enter description"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </>
          ) : (
            <>
              {/* View Mode */}
              <div className="space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    Name
                  </p>
                  <p className="text-base font-medium text-gray-800">
                    {langContent.organisation_name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    Description
                  </p>
                  <p className="text-sm text-gray-600">
                    {langContent.description || '-'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const OrganizationPreviewModal: React.FC<OrganizationPreviewProps> = ({
  open,
  onClose,
  formData,
  source_language,
  onTranslationsGenerated,
  className,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [translateOrganizationContent] =
    useTranslateOrganizationContentMutation();

  // Initialize translations from formData
  const getInitialTranslations = () => {
    if (!formData.translations) return {};

    if (typeof formData.translations === 'string') {
      try {
        return JSON.parse(formData.translations);
      } catch {
        return {};
      }
    }

    return formData.translations;
  };

  const initialTranslations = getInitialTranslations();
  const [translations, setTranslations] =
    useState<
      Record<
        string,
        Partial<{ organisation_name: string; description: string }>
      >
    >(initialTranslations);
  const [isLoading, setIsLoading] = useState(false);
  const [, setHasTranslated] = useState(
    Object.keys(initialTranslations).length > 0
  );
  const [lastFormData, setLastFormData] = useState<string>('');
  const [contentModified, setContentModified] = useState(false);

  const translationsRef = useRef(translations);
  const lastProcessedTranslationsRef = useRef<string | null>(null);
  const onTranslationsGeneratedRef = useRef(onTranslationsGenerated);
  const lastAttemptedTranslationKeyRef = useRef<string | null>(null);

  // Update the ref when translations change
  useEffect(() => {
    translationsRef.current = translations;
  }, [translations]);

  // Update the ref when onTranslationsGenerated changes
  useEffect(() => {
    onTranslationsGeneratedRef.current = onTranslationsGenerated;
  }, [onTranslationsGenerated]);

  // Update translations when formData.translations changes
  useEffect(() => {
    const getInitialTranslations = () => {
      if (!formData.translations) return {};

      if (typeof formData.translations === 'string') {
        try {
          return JSON.parse(formData.translations);
        } catch {
          return {};
        }
      }

      return formData.translations;
    };

    const newTranslations = getInitialTranslations();
    const translationsString = JSON.stringify(newTranslations);

    // Only update if translations have actually changed
    if (lastProcessedTranslationsRef.current !== translationsString) {
      lastProcessedTranslationsRef.current = translationsString;
      const hasExistingTranslations = Object.keys(newTranslations).length > 0;

      setTranslations(newTranslations);
      setHasTranslated(hasExistingTranslations);
    }
  }, [formData.translations]);

  // Notify parent when translations change
  useEffect(() => {
    const hasExistingTranslations = Object.keys(translations).length > 0;
    if (hasExistingTranslations && onTranslationsGeneratedRef.current) {
      onTranslationsGeneratedRef.current(translations);
    }
  }, [translations]);

  // Reset contentModified when modal opens
  useEffect(() => {
    if (open) {
      setContentModified(false);
      lastAttemptedTranslationKeyRef.current = null;
    }
  }, [open]);

  // Track form data changes
  useEffect(() => {
    const currentFormDataString = JSON.stringify({
      organisation_name: formData.organisation_name,
      description: formData.description,
    });

    if (lastFormData && lastFormData !== currentFormDataString) {
      setHasTranslated(false);
      setContentModified(true);
      setLastFormData(currentFormDataString);
      lastAttemptedTranslationKeyRef.current = null;
    } else if (!lastFormData) {
      setLastFormData(currentFormDataString);
    }
  }, [formData.organisation_name, formData.description, lastFormData]);

  const handleEditContent = React.useCallback(
    (languageCode: string, field: keyof EditValues, value: string) => {
      setTranslations((prev) => {
        const newTranslations = { ...prev };
        if (!newTranslations[languageCode]) {
          newTranslations[languageCode] = {};
        }
        newTranslations[languageCode] = {
          ...newTranslations[languageCode],
          [field]: value,
        };

        if (onTranslationsGenerated) {
          onTranslationsGenerated(newTranslations);
        }

        return newTranslations;
      });
    },
    [onTranslationsGenerated]
  );

  const currentFormDataRef = useRef(formData);
  const currentSourceLanguageRef = useRef(source_language);
  const currentCallbackRef = useRef(onTranslationsGenerated);

  useEffect(() => {
    currentFormDataRef.current = formData;
    currentSourceLanguageRef.current = source_language;
    currentCallbackRef.current = onTranslationsGenerated;
  }, [formData, source_language, onTranslationsGenerated]);

  const fetchTranslations = React.useCallback(
    async (forceRegenerate = false) => {
      if (!open) return;

      // Check if we already have translations from formData
      const getExistingTranslations = () => {
        if (!formData.translations) return {};

        if (typeof formData.translations === 'string') {
          try {
            return JSON.parse(formData.translations);
          } catch {
            return {};
          }
        }

        return formData.translations;
      };

      const existingTranslations = getExistingTranslations();
      const hasExistingTranslationsFromFormData =
        hasTranslations(existingTranslations);
      const currentFormData = currentFormDataRef.current;
      const currentSourceLanguage = currentSourceLanguageRef.current;
      const requestKey = JSON.stringify({
        description: currentFormData.description || '',
        source_language: currentSourceLanguage || 'en',
      });

      // Always reuse existing translations if present unless user forces regenerate.
      if (hasExistingTranslationsFromFormData && !forceRegenerate) {
        setTranslations(existingTranslations);
        setHasTranslated(true);
        setContentModified(false);
        const currentCallback = currentCallbackRef.current;
        if (currentCallback) {
          currentCallback(existingTranslations);
        }
        return;
      }

      if (
        !forceRegenerate &&
        !hasExistingTranslationsFromFormData &&
        lastAttemptedTranslationKeyRef.current === requestKey
      ) {
        return;
      }

      // Fetch translations when none exist yet, content changed, or regenerate is requested.
      if (
        !hasExistingTranslationsFromFormData ||
        contentModified ||
        forceRegenerate
      ) {
        try {
          setIsLoading(true);
          lastAttemptedTranslationKeyRef.current = requestKey;

          const dataToTranslate: TranslationPayload = {
            description: currentFormData.description,
            source_language: currentSourceLanguage || 'en',
          };

          const response =
            await translateOrganizationContent(dataToTranslate).unwrap();
          const warningMessage = (response as { warning_message?: unknown })
            ?.warning_message;

          if (typeof warningMessage === 'string' && warningMessage.trim()) {
            enqueueSnackbar(warningMessage, { variant: 'warning' });
          }

          const nextTranslations = hasTranslations(response.translations)
            ? response.translations
            : {};

          setTranslations(nextTranslations);
          setHasTranslated(hasTranslations(nextTranslations));
          setContentModified(false); // Reset after translation attempt

          if (hasTranslations(nextTranslations)) {
            setContentModified(false); // Reset after successful translation

            const currentCallback = currentCallbackRef.current;
            if (currentCallback) {
              currentCallback(nextTranslations);
            }
          }
        } catch (error) {
          console.error('Translation failed:', error);
          const backendMessage = getApiErrorMessage(error);
          if (backendMessage) {
            enqueueSnackbar(backendMessage, { variant: 'error' });
          }
        } finally {
          setIsLoading(false);
        }
      }
    },
    [contentModified, enqueueSnackbar, open, translateOrganizationContent]
  );

  // Translation fetching logic
  useEffect(() => {
    if (!open) {
      setHasTranslated(false);
      setTranslations({});
      setIsLoading(false);
      return;
    }

    void fetchTranslations();
  }, [open, formData.translations, fetchTranslations]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="preview-modal-title"
      aria-describedby="preview-modal-description"
    >
      <div className="harbor-preview-modal bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-hidden">
        <div className="modal-header bg-gray-200 px-6 py-4 border-b border-gray-100">
          <div className="modal-header-row flex justify-between w-full items-center">
            <h1
              id="preview-modal-title"
              className="text-xl font-bold text-gray-800 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {intl.formatMessage({ id: 'ORGANIZATION.PREVIEW.TITLE' })}{' '}
              {/* Assume key */}
            </h1>
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="modal-close text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={intl.formatMessage({
                id: 'ORGANIZATION.BUTTON.CLOSE',
              })}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-900 rounded-lg contrast:bg-blue-950 borde contrast:border-blue-900">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-3 mt-0.5 text-blue-600 dark:text-blue-200 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed contrast:text-blue-200">
                  {intl.formatMessage({
                    id: 'ORGANIZATION.PREVIEW.DESCRIPTION',
                  })}{' '}
                  {/* Assume key */}
                </p>
              </div>
            </div>
          </div>

          <div
            className="lang-list overflow-y-auto max-h-[50vh] pr-2 custom-scrollbar"
            tabIndex={0}
          >
            {I18N_LANGUAGES.map((lang) => (
              <LanguagePreview
                key={lang.code}
                language={lang}
                formData={formData}
                translatedContent={translations}
                isLoading={isLoading}
                onEditContent={
                  onTranslationsGenerated ? handleEditContent : undefined
                }
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-200 border-t border-gray-100">
          <div className="footer-actions-row flex justify-between items-center">
            <button
              type="button"
              onClick={() => void fetchTranslations(true)}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 bg-green-700 contrast:bg-green-800 text-white text-sm font-medium rounded-lg hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Regenerate translations"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {intl.formatMessage({ id: 'COMMON.GENERATING' })}
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {intl.formatMessage({ id: 'COMMON.REGENERATE' })}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors duration-200"
              aria-label={intl.formatMessage({
                id: 'ORGANIZATION.BUTTON.CLOSE',
              })}
            >
              {intl.formatMessage({ id: 'ORGANIZATION.BUTTON.CLOSE' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OrganizationPreviewModal;
